import { useCallback, useMemo } from 'react';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocket from '../../../Hooks/useSocket';
import { Resource, Action } from '../../../Constants/socket';
import { performanceMonitor } from '../../../Utils/performanceMonitor';

/**
 * Handles all message-related socket events (create, update, delete).
 * Subscribes to loaded messages and processes real-time updates.
 * Note: Batching is handled by useOptimizedMessages, no need for double-batching here.
 * 
 * @param {Object} selectedConversation - Currently selected conversation
 * @param {Array} messages - Array of loaded messages
 * @param {Function} addMessage - Function to add a new message
 * @param {Function} updateMessage - Function to update a message (already batched in useOptimizedMessages)
 * @param {Function} removeMessage - Function to remove a message
 * @param {Function} markMessageAsSeen - Function to mark message as seen
 */
const useMessageSocketEvents = (selectedConversation, messages, addMessage, updateMessage, removeMessage, markMessageAsSeen) => {
    const { socket } = useSocket();

    // Subscribe to all currently loaded messages for consistent real-time updates
    const loadedMessageIds = useMemo(() => {
        return messages.map(message => message.id).filter(Boolean);
    }, [messages]);

    // Memoize conversation ID array to prevent subscribe/unsubscribe churn
    // when selectedConversation object reference changes but the ID stays the same
    const conversationIds = useMemo(
        () => selectedConversation?.id ? [selectedConversation.id] : null,
        [selectedConversation?.id]
    );

    // Subscribe to:
    // 1. UPDATES for all currently loaded messages
    // 2. NEW MESSAGES (Create events) for the current conversation (broadcast to conversation room)
    useSocketSubscription(Resource.CONVERSATION, conversationIds, true);
    useSocketSubscription(Resource.MESSAGE, loadedMessageIds, true);

    // Handle new messages
    const handleNewMessage = useCallback((data) => {
        if (data.conversation_id !== selectedConversation?.id) return;
        addMessage(data);

        // Subscribe to the new message from its server_datetime to cover the readiness gap.
        // Between the server creating the message and useSocketSubscription reactively
        // picking up the new ID, the server may have already emitted UPDATE/DELETE events.
        // Subscribing with from_datetime ensures those are replayed.
        if (data.id && data.server_datetime && socket) {
            socket.subscribeToResource(Resource.MESSAGE, [data.id], data.server_datetime);
        }

        // Mark message as seen using the tracking hook
        markMessageAsSeen(data);
    }, [selectedConversation?.id, addMessage, markMessageAsSeen, socket]);

    const handleMessageUpdate = useCallback((data) => {
        if (data.conversation_id === selectedConversation?.id) {
            performanceMonitor.trackSocketEvent('message.update', () => {
                updateMessage(data); // Batching handled by useOptimizedMessages
            });
        }
    }, [selectedConversation?.id, updateMessage]);

    const handleMessageDelete = useCallback((data) => {
        if (data.conversation_id === selectedConversation?.id) {
            performanceMonitor.trackSocketEvent('message.delete', () => {
                removeMessage(data.id);
            });
        }
    }, [selectedConversation?.id, removeMessage]);

    // Register socket event listeners
    useSocketEvent(Resource.MESSAGE, Action.CREATE, handleNewMessage);
    useSocketEvent(Resource.MESSAGE, Action.UPDATE, handleMessageUpdate);
    useSocketEvent(Resource.MESSAGE, Action.DELETE, handleMessageDelete);
};

export default useMessageSocketEvents;
